import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useRooms } from '@/hooks/useRooms';
import { useClients } from '@/hooks/useClients';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { BedDouble, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type RoomType = 'single' | 'shared' | 'communal';
type FilterKey = 'all' | 'open' | 'occupied' | 'full';

interface FacilityRoomsTabProps {
  facilityId: string;
  isDemo?: boolean;
}

const roomTypeLabel: Record<RoomType, string> = {
  single: 'Single',
  shared: 'Shared',
  communal: 'Communal',
};

interface RoomGroup {
  groupKey: string;
  displayNumber: string;
  type: RoomType;
  beds: any[];
}

export function FacilityRoomsTab({ facilityId, isDemo = false }: FacilityRoomsTabProps) {
  const { rooms } = useRooms();
  const { clients } = useClients();
  const queryClient = useQueryClient();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [newRoomType, setNewRoomType] = useState<RoomType>('single');
  const [newRoomCapacity, setNewRoomCapacity] = useState('4');
  const [filter, setFilter] = useState<FilterKey>('all');

  const facilityRooms = useMemo(
    () =>
      (rooms as any[])
        .filter((r) => r.site_id === facilityId)
        .sort((a, b) => {
          const numA = parseInt(a.room_number) || 0;
          const numB = parseInt(b.room_number) || 0;
          if (numA !== numB) return numA - numB;
          return String(a.room_number).localeCompare(String(b.room_number));
        }),
    [rooms, facilityId],
  );

  const groups = useMemo<RoomGroup[]>(() => {
    const map = new Map<string, RoomGroup>();
    for (const r of facilityRooms) {
      const type: RoomType = (r.room_type as RoomType) || 'single';
      let groupKey = `${type}:${r.room_number}`;
      let displayNumber = String(r.room_number);
      if (type === 'shared') {
        const m = String(r.room_number).match(/^(\d+)([a-zA-Z])?$/);
        if (m) {
          groupKey = `shared:${m[1]}`;
          displayNumber = m[1];
        }
      }
      const existing = map.get(groupKey);
      if (existing) {
        existing.beds.push(r);
      } else {
        map.set(groupKey, { groupKey, displayNumber, type, beds: [r] });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const na = parseInt(a.displayNumber) || 0;
      const nb = parseInt(b.displayNumber) || 0;
      if (na !== nb) return na - nb;
      return a.displayNumber.localeCompare(b.displayNumber);
    });
  }, [facilityRooms]);

  const getOccupants = (roomId: string) =>
    (clients as any[]).filter((c) => c.room_id === roomId && c.status === 'checked-in');

  const groupStats = (g: RoomGroup) => {
    let beds = 0;
    let occupied = 0;
    for (const bed of g.beds) {
      const cap = g.type === 'communal' ? bed.capacity || 1 : 1;
      beds += cap;
      occupied += getOccupants(bed.id).length;
    }
    return { beds, occupied, open: beds - occupied };
  };

  const totals = useMemo(() => {
    let beds = 0;
    let occupied = 0;
    for (const g of groups) {
      const s = groupStats(g);
      beds += s.beds;
      occupied += s.occupied;
    }
    return { rooms: groups.length, beds, occupied, open: beds - occupied };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, clients]);

  const filteredGroups = useMemo(() => {
    if (filter === 'all') return groups;
    return groups.filter((g) => {
      const s = groupStats(g);
      if (filter === 'open') return s.open > 0;
      if (filter === 'occupied') return s.occupied > 0;
      if (filter === 'full') return s.open === 0 && s.beds > 0;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, filter, clients]);

  const filterCounts = useMemo(() => {
    const counts = { all: groups.length, open: 0, occupied: 0, full: 0 };
    for (const g of groups) {
      const s = groupStats(g);
      if (s.open > 0) counts.open++;
      if (s.occupied > 0) counts.occupied++;
      if (s.open === 0 && s.beds > 0) counts.full++;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, clients]);

  const handleAddRoom = async () => {
    if (!newRoomNumber) {
      toast.error('Please enter a room number');
      return;
    }
    try {
      if (newRoomType === 'shared') {
        const roomsToInsert = [
          { site_id: facilityId, room_number: `${newRoomNumber}a`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' },
          { site_id: facilityId, room_number: `${newRoomNumber}b`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' },
        ];
        const { error } = await supabase.from('rooms').insert(roomsToInsert);
        if (error) throw error;
        toast.success(`Shared room ${newRoomNumber} created (${newRoomNumber}a, ${newRoomNumber}b)`);
      } else if (newRoomType === 'communal') {
        const capacity = parseInt(newRoomCapacity) || 4;
        const { error } = await supabase.from('rooms').insert({
          site_id: facilityId,
          room_number: newRoomNumber,
          capacity,
          current_occupancy: 0,
          is_occupied: false,
          room_type: 'communal',
        });
        if (error) throw error;
        toast.success(`Communal room ${newRoomNumber} created (${capacity} beds)`);
      } else {
        const { error } = await supabase.from('rooms').insert({
          site_id: facilityId,
          room_number: newRoomNumber,
          capacity: 1,
          current_occupancy: 0,
          is_occupied: false,
          room_type: 'single',
        });
        if (error) throw error;
        toast.success(`Single room ${newRoomNumber} created`);
      }
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      setIsAddOpen(false);
      setNewRoomNumber('');
      setNewRoomType('single');
      setNewRoomCapacity('4');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create room');
    }
  };

  const handleDeleteBed = async (roomId: string, label: string) => {
    if (getOccupants(roomId).length > 0) {
      toast.error('Cannot delete an occupied bed');
      return;
    }
    if (!confirm(`Delete bed ${label}?`)) return;
    try {
      const { error } = await supabase.from('rooms').delete().eq('id', roomId);
      if (error) throw error;
      toast.success(`Bed ${label} deleted`);
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete bed');
    }
  };

  const handleDeleteGroup = async (g: RoomGroup) => {
    const anyOccupied = g.beds.some((b) => getOccupants(b.id).length > 0);
    if (anyOccupied) {
      toast.error('Cannot delete an occupied room');
      return;
    }
    if (!confirm(`Delete Room ${g.displayNumber}?`)) return;
    try {
      const { error } = await supabase
        .from('rooms')
        .delete()
        .in('id', g.beds.map((b) => b.id));
      if (error) throw error;
      toast.success(`Room ${g.displayNumber} deleted`);
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete room');
    }
  };

  const filterChip = (key: FilterKey, label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setFilter(key)}
      className={cn(
        'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
        filter === key
          ? 'bg-foreground text-background border-foreground'
          : 'bg-background text-muted-foreground border-border hover:text-foreground',
      )}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">Rooms</h3>
          <p className="text-sm text-muted-foreground">
            {totals.rooms} {totals.rooms === 1 ? 'room' : 'rooms'} · {totals.beds} beds · {totals.occupied} occupied
          </p>
        </div>
        {!isDemo && (
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Room
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Room</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Room Type</Label>
                  <Select value={newRoomType} onValueChange={(v) => setNewRoomType(v as RoomType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single — 1 bed per room</SelectItem>
                      <SelectItem value="shared">Shared — 2 beds (creates a/b)</SelectItem>
                      <SelectItem value="communal">Communal — multiple beds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Room Number</Label>
                  <Input
                    value={newRoomNumber}
                    onChange={(e) => setNewRoomNumber(e.target.value)}
                    placeholder="e.g. 16"
                  />
                  {newRoomType === 'shared' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Creates beds {newRoomNumber || '?'}a and {newRoomNumber || '?'}b
                    </p>
                  )}
                </div>
                {newRoomType === 'communal' && (
                  <div>
                    <Label>Beds in Room</Label>
                    <Input
                      type="number"
                      value={newRoomCapacity}
                      onChange={(e) => setNewRoomCapacity(e.target.value)}
                      placeholder="4"
                      min="2"
                    />
                  </div>
                )}
                <Button onClick={handleAddRoom} className="w-full">
                  Create Room
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filter toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {filterChip('all', 'All', filterCounts.all)}
          {filterChip('open', 'Open', filterCounts.open)}
          {filterChip('occupied', 'Occupied', filterCounts.occupied)}
          {filterChip('full', 'Full', filterCounts.full)}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/40" /> Open
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-primary" /> Occupied
          </span>
        </div>
      </div>

      {filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {groups.length === 0 ? 'No rooms configured for this facility' : 'No rooms match this filter'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredGroups.map((g) => {
            const stats = groupStats(g);
            const isFull = stats.open === 0 && stats.beds > 0;
            const isEmpty = stats.occupied === 0;

            return (
              <div
                key={g.groupKey}
                className={cn(
                  'group relative rounded-lg border bg-card overflow-hidden',
                  isFull && 'border-primary/40',
                )}
              >
                {/* Left accent strip for occupancy */}
                <div
                  className={cn(
                    'absolute left-0 top-0 bottom-0 w-1',
                    isFull ? 'bg-primary' : isEmpty ? 'bg-muted' : 'bg-primary/40',
                  )}
                />

                {/* Header */}
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-muted/30">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold">Room {g.displayNumber}</span>
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {roomTypeLabel[g.type]}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {stats.occupied}/{stats.beds}{' '}
                      {isFull ? '· Full' : isEmpty ? '· Empty' : ''}
                    </span>
                    {!isDemo && isEmpty && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                        onClick={() => handleDeleteGroup(g)}
                        title={`Delete Room ${g.displayNumber}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Bed tiles */}
                <div
                  className={cn(
                    'p-3 grid gap-2',
                    g.type === 'communal' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
                  )}
                >
                  {g.beds.flatMap((bed) => {
                    const occupants = getOccupants(bed.id);
                    const cap = g.type === 'communal' ? bed.capacity || 1 : 1;
                    // For communal, render `cap` slots; first `occupants.length` filled
                    if (g.type === 'communal') {
                      return Array.from({ length: cap }).map((_, i) => {
                        const occ = occupants[i];
                        const filled = !!occ;
                        return (
                          <BedTile
                            key={`${bed.id}-${i}`}
                            label={`${bed.room_number}·${i + 1}`}
                            filled={filled}
                            occupantName={occ ? (occ.name ?? `${occ.first_name ?? ''} ${occ.last_name ?? ''}`.trim()) : null}
                          />
                        );
                      });
                    }
                    const occ = occupants[0];
                    const filled = !!occ;
                    return [
                      <BedTile
                        key={bed.id}
                        label={String(bed.room_number)}
                        filled={filled}
                        occupantName={occ ? (occ.name ?? `${occ.first_name ?? ''} ${occ.last_name ?? ''}`.trim()) : null}
                        onDelete={!isDemo && !filled ? () => handleDeleteBed(bed.id, bed.room_number) : undefined}
                      />,
                    ];
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BedTile({
  label,
  filled,
  occupantName,
  onDelete,
}: {
  label: string;
  filled: boolean;
  occupantName: string | null;
  onDelete?: () => void;
}) {
  return (
    <div
      className={cn(
        'relative rounded-md border px-2.5 py-2 text-xs transition-colors group/bed flex items-center gap-2',
        filled ? 'bg-primary/5 border-primary/30' : 'bg-background border-border',
      )}
    >
      <span className="font-medium tabular-nums shrink-0 w-8">{label}</span>
      <p
        className={cn(
          'truncate flex-1 min-w-0',
          filled ? 'text-foreground' : 'text-muted-foreground',
        )}
        title={occupantName || undefined}
      >
        {occupantName || '—'}
      </p>
      <span className="flex items-center gap-1 shrink-0">
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            filled ? 'bg-primary' : 'bg-muted-foreground/40',
          )}
        />
        <span className="text-[10px] text-muted-foreground">
          {filled ? 'Occupied' : 'Open'}
        </span>
      </span>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute top-1 right-1 h-5 w-5 rounded inline-flex items-center justify-center text-destructive opacity-0 group-hover/bed:opacity-100 transition-opacity hover:bg-destructive/10 bg-background"
          title={`Delete ${label}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
